import React, { useState, useEffect, useCallback } from 'react';
import { resolveApiBase } from '@/utils/apiBase';
import { 
  Plus, 
  Search, 
  ShieldCheck, 
  User, 
  Ban, 
  Edit2, 
  X,
  CheckCircle2,
  Loader2,
  AlertTriangle,
  Eye,
  RefreshCw
} from 'lucide-react';
import { Role, User as UserType } from '@/types';
import { ToastContainer, useToast } from '../ui/Toast';

// Para o app do tenant, usar a API do Core (isolada por tenant)
const CORE_API = resolveApiBase(import.meta.env.VITE_CORE_API, '/core', 'http://localhost:3001');

interface UsersPageProps {
  darkMode: boolean;
}

// Mapeamento de tipos do banco para roles do frontend
type DbTipo = 'ADMIN' | 'STAFF' | 'CLIENTE';
const tipoToRole: Record<DbTipo, Role> = {
  'ADMIN': 'admin',
  'STAFF': 'staff',
  'CLIENTE': 'client'
};
const roleToTipo: Record<Role, DbTipo> = {
  'admin': 'ADMIN',
  'staff': 'STAFF',
  'client': 'CLIENTE'
};

// Extend UserType locally for the UI state including status
interface UserWithStatus extends UserType {
  status: 'active' | 'blocked';
  whatsapp?: string;
}

// ID do usuário master (não pode ser editado/bloqueado)
const MASTER_USER_ID = 1;

// Funções de máscara/normalização para WhatsApp (E.164 BR: 55 + DDD + 9 dígitos)
const normalizeWhatsapp = (value: string): string => {
  const digits = value.replace(/\D/g, '');
  if (!digits) return '';

  let local = digits.replace(/^0+/, '');
  if (local.startsWith('55')) {
    local = local.slice(2);
  }
  local = local.replace(/^0+/, '');

  if (local.length > 11) {
    local = local.slice(0, 11); // preserva DDD+numero e descarta sufixos/ramais
  }

  const trimmed = local.slice(-11);
  return trimmed ? `55${trimmed}` : '';
};

const formatWhatsApp = (value: string): string => {
  const normalized = normalizeWhatsapp(value);
  const local = normalized.startsWith('55') ? normalized.slice(2) : normalized;
  const d = local.slice(0, 11);

  if (d.length <= 2) return d ? `(${d}` : '';
  if (d.length <= 7) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
};

export const UsersPage: React.FC<UsersPageProps> = ({ darkMode }) => {
  const [users, setUsers] = useState<UserWithStatus[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null); // erros gerais da página
  const [formError, setFormError] = useState<string | null>(null); // erros do modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState<UserWithStatus | null>(null);
  
  // Form State
  const [formData, setFormData] = useState<Partial<UserWithStatus>>({
    name: '',
    email: '',
    whatsapp: '',
    role: 'staff',
    status: 'active'
  });

  // Search State
  const [searchTerm, setSearchTerm] = useState('');

  // Toasts
  const { toasts, toast, removeToast } = useToast();

  // --- FETCH DADOS DA API REAL ---
  const fetchUsers = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('va_token');
      const response = await fetch(`${CORE_API}/usuarios`, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      
      if (!response.ok) {
        throw new Error(`Falha ao carregar dados: ${response.statusText}`);
      }
      
      const data = await response.json();
      
      // Converte formato do banco para formato do frontend
      const convertedUsers: UserWithStatus[] = (data.usuarios || []).map((u: any) => ({
        id: u.id,
        name: u.nome,
        email: u.email,
        whatsapp: normalizeWhatsapp(u.whatsapp || ''),
        role: tipoToRole[u.tipo as DbTipo] || 'staff',
        status: u.ativo ? 'active' : 'blocked'
      }));
      
      setUsers(convertedUsers);
    } catch (error) {
      console.error("Erro ao buscar usuários:", error);
      setError("Não foi possível carregar a lista de usuários.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleOpenModal = (user?: UserWithStatus) => {
    setFormError(null);
    if (user && Number(user.id) === MASTER_USER_ID) {
      // Master não pode ser editado
      return;
    }
    if (user) {
      setCurrentUser(user);
      setFormData({
        ...user,
        whatsapp: normalizeWhatsapp(user.whatsapp || ''),
      });
    } else {
      setCurrentUser(null);
      setFormData({ name: '', email: '', whatsapp: '', role: 'staff', status: 'active' });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setFormError(null);
    setIsModalOpen(false);
    setCurrentUser(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.email) return;

    try {
      setFormError(null);
      const digits = (formData.whatsapp || '').replace(/\D/g, '');
      if (digits.length < 10) {
        throw new Error('Informe o WhatsApp com DDD + número (ex: 11999999999). O DDI 55 será adicionado automaticamente.');
      }
      const normalizedWhatsapp = normalizeWhatsapp(formData.whatsapp || '');
      if (!normalizedWhatsapp) {
        throw new Error('WhatsApp é obrigatório.');
      }

      if (currentUser) {
        // Edit via API
        const token = localStorage.getItem('va_token');
        const response = await fetch(`${CORE_API}/usuarios/${currentUser.id}`, {
          method: 'PUT',
          headers: { 
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            nome: formData.name,
            email: formData.email,
            whatsapp: normalizedWhatsapp || null,
            tipo: roleToTipo[formData.role as Role]
          })
        });
        
        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.error || 'Erro ao atualizar');
        }
        await fetchUsers();
        handleCloseModal();
        toast.success('Usuário atualizado com sucesso');
      } else {
        // Add via API
        const token = localStorage.getItem('va_token');
        const response = await fetch(`${CORE_API}/usuarios`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            nome: formData.name,
            email: formData.email,
            whatsapp: normalizedWhatsapp || null,
            senha: '123456', // Senha padrão - usuário deve alterar
            tipo: roleToTipo[formData.role as Role]
          })
        });
        
        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.error || 'Erro ao criar');
        }
        await fetchUsers();
        handleCloseModal(); // fecha apenas na criação
        toast.success('Usuário criado com sucesso');
      }
    } catch (error: any) {
      setFormError(error.message || 'Erro ao salvar usuário');
    }
  };

  const handleBlockUser = async (id: number | string) => {
    if (id === MASTER_USER_ID) return;
    try {
      const token = localStorage.getItem('va_token');
      const response = await fetch(`${CORE_API}/usuarios/${id}/toggle-status`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      
      if (!response.ok) {
        throw new Error('Erro ao alterar status');
      }
      
      // Recarrega lista
      await fetchUsers();
    } catch (error: any) {
      setError(error.message || 'Erro ao alterar status');
    }
  };

  const handleResetPassword = async (id: number | string) => {
    if (!confirm('Tem certeza que deseja resetar a senha deste usuário?')) return;
    
    try {
      const token = localStorage.getItem('va_token');
      const response = await fetch(`${CORE_API}/usuarios/${id}/reset-password`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Erro ao resetar senha');
      }
      
      // Mostra mensagem de sucesso
      alert(data.message + (data.novaSenha ? `\n\nNova senha: ${data.novaSenha}` : ''));
      
    } catch (error: any) {
      setError(error.message || 'Erro ao resetar senha');
    }
  };

  // Filtrar usuários pelo termo de busca
  const filteredUsers = users.filter(user => 
    user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (user.whatsapp && user.whatsapp.includes(searchTerm))
  );

  return (
    <div className={`p-6 md:p-8 min-h-full ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>
      <ToastContainer toasts={toasts} onRemove={removeToast} />
      
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
        <div>
          <h1 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-vision-green'}`}>
            Gestão de Usuários
          </h1>
          <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Gerencie o acesso e permissões dos colaboradores.
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          <button 
            onClick={() => fetchUsers()}
            disabled={isLoading}
            className={`
              p-2.5 rounded-lg border transition-all disabled:opacity-50
              ${darkMode 
                ? 'border-gray-700 hover:bg-gray-800 text-gray-400' 
                : 'border-gray-200 hover:bg-gray-50 text-gray-500'
              }
            `}
            title="Atualizar lista"
          >
            <RefreshCw size={18} className={isLoading ? 'animate-spin' : ''} />
          </button>
          <button 
            onClick={() => handleOpenModal()}
            disabled={isLoading}
            className={`
              flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-all shadow-lg active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed
              ${darkMode 
                ? 'bg-vision-activeRed text-white hover:bg-red-700' 
                : 'bg-[#294b29] text-white hover:bg-[#1a301a]'
              }
            `}
          >
            <Plus size={20} />
            <span>Novo Usuário</span>
          </button>
        </div>
      </div>

      {/* Main Content Card */}
      <div className={`
        rounded-xl border shadow-sm overflow-hidden min-h-[400px] flex flex-col
        ${darkMode ? 'bg-vision-darkGreen border-vision-border' : 'bg-white border-gray-200'}
      `}>
        
        {/* Filters / Search Bar */}
        <div className={`p-4 border-b flex items-center gap-3 ${darkMode ? 'border-vision-border' : 'border-gray-100'}`}>
           <div className="relative flex-1 max-w-sm">
             <Search className={`absolute left-3 top-2.5 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} size={18} />
             <input 
                type="text" 
                placeholder="Buscar por nome, email ou WhatsApp..." 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={`
                  w-full pl-10 pr-4 py-2 rounded-lg border focus:ring-2 outline-none text-sm transition-all
                  ${darkMode 
                    ? 'bg-vision-surface border-vision-border text-white focus:ring-gray-600' 
                    : 'bg-gray-50 border-gray-300 text-gray-800 focus:ring-[#294b29] focus:border-transparent'
                  }
                `}
             />
           </div>
           {searchTerm && (
             <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
               {filteredUsers.length} resultado{filteredUsers.length !== 1 ? 's' : ''}
             </span>
           )}
        </div>

        {/* Loading / Error / Table */}
        {isLoading ? (
          <div className="flex-1 flex flex-col items-center justify-center p-12 text-gray-500 animate-in fade-in">
             <Loader2 className={`w-10 h-10 animate-spin mb-4 ${darkMode ? 'text-vision-green' : 'text-[#294b29]'}`} />
             <p>Carregando usuários...</p>
          </div>
        ) : error ? (
           <div className="flex-1 flex flex-col items-center justify-center p-12 text-red-500 animate-in fade-in">
             <AlertTriangle className="w-10 h-10 mb-4" />
             <p>{error}</p>
             <button 
               onClick={() => window.location.reload()} 
               className="mt-4 text-sm underline hover:text-red-700"
             >
               Tentar novamente
             </button>
          </div>
        ) : (
          <div className="overflow-x-auto flex-1">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className={`text-xs uppercase tracking-wider ${darkMode ? 'bg-vision-surface text-gray-400' : 'bg-gray-50 text-gray-600'}`}>
                  <th className="px-6 py-4 font-semibold">Usuário</th>
                  <th className="px-6 py-4 font-semibold">WhatsApp</th>
                  <th className="px-6 py-4 font-semibold text-center">Permissão</th>
                  <th className="px-6 py-4 font-semibold text-center">Status</th>
                  <th className="px-6 py-4 font-semibold text-right">Ações</th>
                </tr>
              </thead>
              <tbody className={`divide-y ${darkMode ? 'divide-vision-border' : 'divide-gray-200'}`}>
                {filteredUsers.map((user) => (
                  <tr 
                    key={user.id} 
                    className={`
                      transition-colors
                      ${darkMode ? 'hover:bg-vision-surface' : 'hover:bg-gray-50'}
                    `}
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className={`
                          w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm
                          ${darkMode ? 'bg-vision-surface text-gray-300' : 'bg-green-100 text-vision-green'}
                        `}>
                          {user.name.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className={`font-medium ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>{user.name}</p>
                          <p className="text-xs text-gray-500 md:hidden">{user.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className={`px-6 py-4 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      {formatWhatsApp(user.whatsapp || '')}
                    </td>
                    <td className="px-6 py-4 text-center">
                      <div className="inline-flex items-center justify-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border border-transparent">
                        {user.role === 'admin' ? (
                          <span className={`flex items-center gap-1 ${darkMode ? 'text-red-400' : 'text-[#8b0000]'}`}>
                            <ShieldCheck size={16} />
                            Admin
                          </span>
                        ) : user.role === 'client' ? (
                          <span className={`flex items-center gap-1 ${darkMode ? 'text-purple-400' : 'text-purple-700'}`}>
                            <Eye size={16} />
                            Cliente
                          </span>
                        ) : (
                          <span className={`flex items-center gap-1 ${darkMode ? 'text-blue-400' : 'text-blue-700'}`}>
                            <User size={16} />
                            Staff
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className={`
                        inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
                        ${user.status === 'active' 
                          ? (darkMode ? 'bg-green-900/30 text-green-400' : 'bg-green-100 text-green-800')
                          : (darkMode ? 'bg-red-900/30 text-red-400' : 'bg-red-100 text-red-800')
                        }
                      `}>
                        {user.status === 'active' ? 'Ativo' : 'Bloqueado'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      {user.id === MASTER_USER_ID ? (
                        <span className={`text-xs font-medium px-2 py-1 rounded ${
                          darkMode ? 'bg-yellow-900/30 text-yellow-400' : 'bg-yellow-100 text-yellow-700'
                        }`}>
                          🔒 Master
                        </span>
                      ) : (
                        <div className="flex items-center justify-end gap-2">
                          <button 
                            onClick={() => handleOpenModal(user)}
                            className={`p-2 rounded-lg transition-colors ${darkMode ? 'hover:bg-vision-surface text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}
                            title="Editar"
                          >
                            <Edit2 size={16} />
                          </button>
                          <button 
                            onClick={() => user.id && handleBlockUser(user.id)}
                            className={`
                              p-2 rounded-lg transition-colors 
                              ${user.status === 'blocked' 
                                  ? (darkMode ? 'bg-red-900/50 text-red-400' : 'bg-red-100 text-red-600') 
                                  : (darkMode ? 'hover:bg-red-900/30 text-red-400' : 'hover:bg-red-50 text-red-600')
                              }
                            `}
                            title={user.status === 'active' ? "Bloquear Acesso" : "Desbloquear Acesso"}
                          >
                            <Ban size={16} />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            
            {filteredUsers.length === 0 && (
              <div className="p-12 text-center text-gray-500">
                {searchTerm ? 'Nenhum usuário encontrado com esse termo.' : 'Nenhum usuário cadastrado.'}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className={`
            w-full max-w-md max-h-[90vh] rounded-2xl shadow-2xl transform transition-all scale-100 flex flex-col
            ${darkMode ? 'bg-vision-darkGreen text-white border border-vision-border' : 'bg-white text-gray-900'}
          `}>
            <div className={`flex items-center justify-between p-4 border-b shrink-0 ${darkMode ? 'border-vision-border' : 'border-gray-100'}`}>
              <h3 className="text-lg font-bold">
                {currentUser ? 'Editar Usuário' : 'Novo Usuário'}
              </h3>
              <button 
                onClick={handleCloseModal}
                className={`p-1 rounded-full transition-colors ${darkMode ? 'hover:bg-vision-surface' : 'hover:bg-gray-100'}`}
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-4 space-y-3 overflow-y-auto">
              {formError && (
                <div className="p-2 rounded bg-red-100 text-red-700 text-sm border border-red-200">
                  {formError}
                </div>
              )}
              <div className="space-y-1">
                <label className="text-sm font-medium ml-1">Nome Completo</label>
                <input 
                  type="text" 
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className={`
                    w-full px-4 py-2 rounded-lg border outline-none focus:ring-2 transition-all
                    ${darkMode 
                      ? 'bg-vision-surface border-vision-border focus:ring-gray-600' 
                      : 'bg-white border-gray-300 focus:ring-[#294b29] focus:border-transparent'
                    }
                  `}
                  placeholder="Ex: João da Silva"
                  required
                />
              </div>

              <div className="space-y-1">
                <label className="text-sm font-medium ml-1">E-mail Corporativo</label>
                <input 
                  type="email" 
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className={`
                    w-full px-4 py-2 rounded-lg border outline-none focus:ring-2 transition-all
                    ${darkMode 
                      ? 'bg-vision-surface border-vision-border focus:ring-gray-600' 
                      : 'bg-white border-gray-300 focus:ring-[#294b29] focus:border-transparent'
                    }
                  `}
                  placeholder="Ex: joao@visaoalpha.com"
                  required
                />
              </div>

              <div className="space-y-1">
                <label className="text-sm font-medium ml-1">WhatsApp <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>(para notificações)</span></label>
                <input 
                  type="tel" 
              value={formatWhatsApp(formData.whatsapp || '')}
              onChange={(e) => setFormData({...formData, whatsapp: normalizeWhatsapp(e.target.value)})}
                  className={`
                    w-full px-4 py-2 rounded-lg border outline-none focus:ring-2 transition-all
                    ${darkMode 
                      ? 'bg-vision-surface border-vision-border focus:ring-gray-600' 
                      : 'bg-white border-gray-300 focus:ring-[#294b29] focus:border-transparent'
                    }
                  `}
                  placeholder="(11) 99999-9999"
              required
                />
              </div>

              <div className="space-y-1">
                <label className="text-sm font-medium ml-1">Nível de Acesso</label>
                <div className="grid grid-cols-3 gap-2">
                  <button
                    type="button"
                    onClick={() => setFormData({...formData, role: 'admin'})}
                    className={`
                      flex flex-col items-center justify-center p-2 rounded-lg border-2 transition-all gap-1
                      ${formData.role === 'admin' 
                        ? (darkMode ? 'border-vision-activeRed bg-red-900/20 text-white' : 'border-[#8b0000] bg-red-50 text-[#8b0000]') 
                        : (darkMode ? 'border-vision-border hover:bg-vision-surface' : 'border-gray-200 hover:bg-gray-50')
                      }
                    `}
                  >
                    <ShieldCheck size={20} />
                    <span className="text-xs font-semibold">Admin</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({...formData, role: 'staff'})}
                    className={`
                      flex flex-col items-center justify-center p-2 rounded-lg border-2 transition-all gap-1
                      ${formData.role === 'staff' 
                        ? (darkMode ? 'border-blue-500 bg-blue-900/20 text-white' : 'border-[#294b29] bg-green-50 text-[#294b29]') 
                        : (darkMode ? 'border-vision-border hover:bg-vision-surface' : 'border-gray-200 hover:bg-gray-50')
                      }
                    `}
                  >
                    <User size={20} />
                    <span className="text-xs font-semibold">Staff</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({...formData, role: 'client'})}
                    className={`
                      flex flex-col items-center justify-center p-2 rounded-lg border-2 transition-all gap-1
                      ${formData.role === 'client' 
                        ? (darkMode ? 'border-purple-500 bg-purple-900/20 text-white' : 'border-purple-600 bg-purple-50 text-purple-700') 
                        : (darkMode ? 'border-vision-border hover:bg-vision-surface' : 'border-gray-200 hover:bg-gray-50')
                      }
                    `}
                  >
                    <Eye size={20} />
                    <span className="text-xs font-semibold">Cliente</span>
                  </button>
                </div>
              </div>

              <div className="pt-2 space-y-2">
                <button 
                  type="submit"
                  className={`
                    w-full py-2.5 rounded-lg font-bold text-white flex items-center justify-center gap-2 transition-all active:scale-95
                    ${darkMode ? 'bg-vision-activeRed hover:bg-red-700' : 'bg-[#294b29] hover:bg-[#1a301a]'}
                  `}
                >
                  <CheckCircle2 size={18} />
                  <span>{currentUser ? 'Salvar Alterações' : 'Cadastrar Usuário'}</span>
                </button>
                
                {/* Botão Reset Senha - só aparece ao editar */}
                {currentUser && (
                  <button 
                    type="button"
                    onClick={() => {
                      handleCloseModal();
                      handleResetPassword(currentUser.id!);
                    }}
                    className={`
                      w-full py-2 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-all border
                      ${darkMode 
                        ? 'border-yellow-700 text-yellow-500 hover:bg-yellow-900/20' 
                        : 'border-yellow-500 text-yellow-700 hover:bg-yellow-50'
                      }
                    `}
                  >
                    <RefreshCw size={16} />
                    <span>Resetar Senha {formData.whatsapp ? '(via WhatsApp)' : ''}</span>
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};

